/**
 * /admin/stripe-disputes — disputas Stripe abiertas + análisis interno persistido.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import {
  BRAND_NAME,
  BRAND_PERSON_NAME,
  BRAND_SITE_URL,
  BRAND_SUPPORT_EMAIL,
} from "@/lib/brandConfig";
import {
  buildDisputeDossierHtml,
  openDisputeDossierPrintWindow,
} from "@/lib/disputeDossierPrint";
import { cn } from "@/lib/utils";
import { useUser } from "@clerk/clerk-react";
import {
  ExternalLink,
  Loader2,
  Printer,
  RefreshCw,
  Save,
  AlertTriangle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

interface StripeDisputePublic {
  id: string;
  amount: number;
  currency: string;
  status: string;
  reason: string | null;
  created: number;
  due_by: number | null;
  charge_id: string | null;
  payment_intent_id: string | null;
  customer_email: string | null;
  is_charge_refundable: boolean | null;
}

interface AnalysisRow {
  stripe_dispute_id: string;
  analysis_notes: string | null;
  evidence_summary: string | null;
  internal_status: string;
  updated_by_email: string | null;
  updated_at: string;
  last_synced_at: string | null;
}

interface DisputeItem {
  stripe: StripeDisputePublic;
  analysis: AnalysisRow | null;
}

const INTERNAL_STATUSES = [
  { value: "draft", label: "Borrador" },
  { value: "ready", label: "Listo para presentar" },
  { value: "submitted", label: "Evidencia enviada" },
  { value: "archived", label: "Archivado" },
] as const;

function formatMoney(amountCents: number, currency: string): string {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 2,
  }).format(amountCents / 100);
}

function formatUnix(ts: number | null): string {
  if (ts == null) return "—";
  try {
    return new Date(ts * 1000).toLocaleString("es-AR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function formatIsoDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-AR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function stripeDashboardDisputeUrl(disputeId: string): string {
  const pk = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
  const isTest = typeof pk === "string" && pk.startsWith("pk_test");
  return isTest
    ? `https://dashboard.stripe.com/test/disputes/${disputeId}`
    : `https://dashboard.stripe.com/disputes/${disputeId}`;
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case "needs_response":
    case "warning_needs_response":
      return "bg-amber-500/15 text-amber-700 dark:text-amber-400";
    case "under_review":
      return "bg-blue-500/15 text-blue-700 dark:text-blue-400";
    case "won":
      return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";
    case "lost":
    case "charge_refunded":
      return "bg-red-500/15 text-red-700 dark:text-red-400";
    default:
      return "bg-zinc-500/15 text-zinc-600 dark:text-zinc-400";
  }
}

export default function StripeDisputesAdmin() {
  const { toast } = useToast();
  const { user } = useUser();
  const { getAuthHeaders, isLoaded } = useAdminAuth();

  const [scope, setScope] = useState<"open" | "recent">("open");
  const [savingId, setSavingId] = useState<string | null>(null);
  const [items, setItems] = useState<DisputeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [partialErrors, setPartialErrors] = useState<string[]>([]);
  const [listTruncated, setListTruncated] = useState(false);
  const [analysesUnavailable, setAnalysesUnavailable] = useState(false);
  const [analysesUnavailableReason, setAnalysesUnavailableReason] = useState<string | null>(null);

  /** Campos locales por dispute id (edición antes de guardar) */
  const [drafts, setDrafts] = useState<
    Record<
      string,
      {
        analysis_notes: string;
        evidence_summary: string;
        internal_status: string;
      }
    >
  >({});

  const load = useCallback(async () => {
    if (!isLoaded) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `/api/admin/stripe-disputes?scope=${encodeURIComponent(scope)}`,
        { headers },
      );
      const data = (await res.json()) as {
        error?: string;
        disputes?: DisputeItem[];
        partial_errors?: string[];
        list_truncated?: boolean;
        analyses_unavailable?: boolean;
        analyses_unavailable_reason?: string;
      };
      if (!res.ok) {
        throw new Error(data.error || res.statusText);
      }
      const list = data.disputes ?? [];
      setPartialErrors(data.partial_errors ?? []);
      setListTruncated(Boolean(data.list_truncated));
      setAnalysesUnavailable(Boolean(data.analyses_unavailable));
      setAnalysesUnavailableReason(
        typeof data.analyses_unavailable_reason === "string"
          ? data.analyses_unavailable_reason
          : null,
      );
      setItems(list);
      const nextDrafts: Record<string, (typeof drafts)[string]> = {};
      for (const row of list) {
        const id = row.stripe.id;
        nextDrafts[id] = {
          analysis_notes: row.analysis?.analysis_notes ?? "",
          evidence_summary: row.analysis?.evidence_summary ?? "",
          internal_status:
            row.analysis?.internal_status &&
            INTERNAL_STATUSES.some((s) => s.value === row.analysis.internal_status)
              ? row.analysis.internal_status
              : "draft",
        };
      }
      setDrafts(nextDrafts);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast({
        variant: "destructive",
        title: "Error al cargar disputas",
        description: msg,
      });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, isLoaded, scope, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const emptyMessage = useMemo(() => {
    if (scope === "open") {
      return "No hay disputas abiertas que requieran acción (needs_response, warning_needs_response, under_review).";
    }
    return "No se encontraron disputas recientes.";
  }, [scope]);

  async function save(disputeId: string) {
    const d = drafts[disputeId];
    if (!d) return;
    setSavingId(disputeId);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/stripe-disputes", {
        method: "PUT",
        headers,
        body: JSON.stringify({
          stripe_dispute_id: disputeId,
          analysis_notes: d.analysis_notes,
          evidence_summary: d.evidence_summary,
          internal_status: d.internal_status,
        }),
      });
      const data = (await res.json()) as { error?: string; analysis?: AnalysisRow };
      if (!res.ok) {
        throw new Error(data.error || res.statusText);
      }
      toast({ title: "Análisis guardado" });
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({
        variant: "destructive",
        title: "No se pudo guardar",
        description: msg,
      });
    } finally {
      setSavingId(null);
    }
  }

  function printDossierPdf(s: StripeDisputePublic) {
    const d = drafts[s.id];
    const statusLabel =
      INTERNAL_STATUSES.find((x) => x.value === (d?.internal_status ?? "draft"))
        ?.label ??
      (d?.internal_status ?? "draft");
    const html = buildDisputeDossierHtml({
      merchantName: BRAND_NAME || "—",
      merchantPersonName: BRAND_PERSON_NAME || "",
      merchantUrl: BRAND_SITE_URL || "—",
      supportEmail: BRAND_SUPPORT_EMAIL || "",
      generatedAtIso: new Date().toISOString(),
      generatedByEmail: user?.primaryEmailAddress?.emailAddress ?? null,
      stripeDashboardDisputeUrl: stripeDashboardDisputeUrl(s.id),
      stripe: {
        id: s.id,
        amountCents: s.amount,
        currency: s.currency,
        status: s.status,
        reason: s.reason,
        createdUnix: s.created,
        dueByUnix: s.due_by,
        chargeId: s.charge_id,
        paymentIntentId: s.payment_intent_id,
        customerEmail: s.customer_email,
      },
      analysisNotes: d?.analysis_notes ?? "",
      evidenceSummary: d?.evidence_summary ?? "",
      internalStatus: statusLabel,
    });
    const ok = openDisputeDossierPrintWindow(html);
    if (!ok) {
      toast({
        variant: "destructive",
        title: "No se abrió la ventana de impresión",
        description:
          "Permití ventanas emergentes para este sitio o reintentá desde otro navegador.",
      });
    }
  }

  return (
    <div className="admin-page mx-auto max-w-4xl space-y-8">
      <AdminPageHeader
        description={
          <>
            {BRAND_NAME ? (
              <>
                <strong className="text-foreground">{BRAND_NAME}</strong>
                {" · "}
              </>
            ) : null}
            Chargebacks abiertos + notas internas. Series de cobros → Revenue.
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Label className="sr-only" htmlFor="scope">
          Alcance
        </Label>
        <Select
          value={scope}
          onValueChange={(v) => setScope(v as "open" | "recent")}
        >
          <SelectTrigger id="scope" className="w-[260px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Solo abiertas / en curso</SelectItem>
            <SelectItem value="recent">Últimas (cualquier estado)</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Actualizar</span>
        </Button>
      </div>

      {analysesUnavailable && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No se pudieron cargar los análisis guardados</AlertTitle>
          <AlertDescription>
            {analysesUnavailableReason ??
              "Revisá que la migración stripe_dispute_analyses esté aplicada en Postgres. Las disputas de Stripe pueden seguir mostrándose arriba; guardar puede fallar hasta corregir la base."}
          </AlertDescription>
        </Alert>
      )}

      {listTruncated && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Listado posiblemente incompleto</AlertTitle>
          <AlertDescription>
            Se alcanzó el límite de páginas al consultar Stripe en el alcance &quot;abiertas&quot;. Si tenés muchas disputas históricas, algunas abiertas podrían quedar fuera del barrido; revisá también el alcance &quot;Últimas&quot; o el Dashboard de Stripe.
          </AlertDescription>
        </Alert>
      )}

      {partialErrors.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Listado parcial desde Stripe</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 list-inside list-disc text-sm">
              {partialErrors.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {loading && items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <ul className="space-y-6">
          {items.map(({ stripe: s, analysis: a }) => {
            const draft = drafts[s.id] ?? {
              analysis_notes: "",
              evidence_summary: "",
              internal_status: "draft",
            };
            return (
              <li key={s.id}>
                <Card>
                  <CardHeader className="space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <CardTitle className="font-mono text-base">{s.id}</CardTitle>
                        <CardDescription className="flex flex-wrap items-center gap-2 pt-1">
                          <span>{formatMoney(s.amount, s.currency)}</span>
                          <span>·</span>
                          <span>{formatUnix(s.created)}</span>
                          {s.reason && (
                            <>
                              <span>·</span>
                              <span className="capitalize">{s.reason.replace(/_/g, " ")}</span>
                            </>
                          )}
                        </CardDescription>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={cn("font-normal", statusBadgeClass(s.status))}>
                          {s.status.replace(/_/g, " ")}
                        </Badge>
                        <Button variant="ghost" size="sm" asChild className="h-8 gap-1 px-2">
                          <a
                            href={stripeDashboardDisputeUrl(s.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Stripe
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1 px-2"
                          onClick={() => printDossierPdf(s)}
                          title="Abre un dossier listo para Archivo → Imprimir → Guardar como PDF"
                        >
                          <Printer className="h-3.5 w-3.5" />
                          Dossier PDF
                        </Button>
                      </div>
                    </div>
                    <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                      {s.customer_email && (
                        <p>
                          <span className="font-medium text-foreground">Email: </span>
                          {s.customer_email}
                        </p>
                      )}
                      {s.charge_id && (
                        <p className="font-mono">
                          charge <span className="text-foreground">{s.charge_id}</span>
                        </p>
                      )}
                      {s.payment_intent_id && (
                        <p className="font-mono sm:col-span-2">
                          payment_intent{" "}
                          <span className="text-foreground">{s.payment_intent_id}</span>
                        </p>
                      )}
                      {s.due_by != null && (
                        <p className="sm:col-span-2 text-amber-700 dark:text-amber-400">
                          Evidencia hasta: {formatUnix(s.due_by)}
                        </p>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 border-t border-border/60 pt-4">
                    <div className="grid gap-2">
                      <Label htmlFor={`notes-${s.id}`}>Análisis interno</Label>
                      <Textarea
                        id={`notes-${s.id}`}
                        rows={4}
                        placeholder="Cadena pago → usuario → activación → uso. Motivo del dispute y puntos a evidenciar."
                        value={draft.analysis_notes}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [s.id]: { ...draft, analysis_notes: e.target.value },
                          }))
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor={`evidence-${s.id}`}>Resumen de evidencia enviada / a enviar</Label>
                      <Textarea
                        id={`evidence-${s.id}`}
                        rows={3}
                        placeholder="Qué se adjuntó o se adjuntará en Stripe (sin datos sensibles innecesarios)."
                        value={draft.evidence_summary}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [s.id]: { ...draft, evidence_summary: e.target.value },
                          }))
                        }
                      />
                    </div>
                    <div className="flex flex-wrap items-end gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor={`status-${s.id}`}>Estado interno</Label>
                        <Select
                          value={draft.internal_status}
                          onValueChange={(v) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [s.id]: { ...draft, internal_status: v },
                            }))
                          }
                        >
                          <SelectTrigger id={`status-${s.id}`} className="w-[220px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {INTERNAL_STATUSES.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        onClick={() => void save(s.id)}
                        disabled={savingId === s.id}
                        className="gap-2"
                      >
                        {savingId === s.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Guardar análisis
                      </Button>
                    </div>
                    {(a?.updated_at || a?.updated_by_email) && (
                      <p className="text-xs text-muted-foreground">
                        Última edición: {formatIsoDateTime(a?.updated_at)}
                        {a?.updated_by_email ? ` · ${a.updated_by_email}` : ""}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
